import React, { useState } from 'react';

import InfiniteScroll from 'react-infinite-scroll-component';
import { FormattedMessage, useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { useInfiniteQuery, useMutation, useQueryClient } from 'react-query';
import { Button, Dropdown } from 'react-bootstrap';
import Spinner from '../../components/Spinner/Spinner';
import useNotifications from '../../hooks/useNotifications';
import Header from '../../components/Header/Header';
import campaignFileService from '../../services/campaignFileService';
import TableHeader from '../../components/Table/TableHeader/TableHeader';
import ListFileUploadSortColumn from '../../models/FileUpload/ListFileUploadSortColumn';
import GenericFormattedDate from '../../components/GenericFormattedDate/GenericFormattedDate';
import CampaignFileStatus from '../../models/FileUpload/CampaignFileStatus';
import UploadFileModal from './UploadFileModal/UploadFileModal';
import CampaignFileItem from '../../models/FileUpload/CampaignFileItem';
import Confirm from '../../components/Confirm/Confirm';
import DownloadFileDropdownItem from '../../components/DownloadFileDropdownItem/DownloadFileDropdownItem';
import ActionsDropdown from '../../components/ActionsDropdown/ActionsDropdown';
import TitleTooltip from '../../components/Tooltips/TitleTooltip/TitleTooltip';
import FileUploadListSkeleton from './FileUploadListSkeleton';
import './FileUploadList.scss';
import useSortingViewState from '../../hooks/useSortingViewState';

const pageSize = 20;

const columns: { name: ListFileUploadSortColumn | ''; key: string }[] = [
  { name: 'createdAt', key: 'fileUploadList.table.header.createdAt' },
  { name: 'productName', key: 'fileUploadList.table.header.productName' },
  { name: 'campaignName', key: 'fileUploadList.table.header.campaignName' },
  { name: 'fileName', key: 'fileUploadList.table.header.fileName' },
  { name: 'type', key: 'fileUploadList.table.header.type' },
  { name: 'comment', key: 'fileUploadList.table.header.comment' },
  { name: 'status', key: 'fileUploadList.table.header.status' },
  { name: '', key: '' }, // action
];

const FileUploadList = () => {
  const { formatMessage: fm } = useIntl();
  const queryClient = useQueryClient();
  const { notify, notifySuccess, notifyError } = useNotifications();
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
  const [isFileDownloading, setFileDownloading] = useState<boolean>(false);
  const { getSortOrder } = useSortingViewState();

  const {
    data: filesData,
    isLoading: isFilesLoading,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery(
    ['campaignFiles', getSortOrder<ListFileUploadSortColumn>('createdAt', 'FileUpload', false)],
    async ({ pageParam = 1 }) => {
      const data = await campaignFileService.manufacturerList({
        paging: { pageSize, pageNumber: pageParam },
        orderBy: getSortOrder<ListFileUploadSortColumn>('createdAt', 'FileUpload', false).toString(),
      });
      return {
        ...data,
        nextPage: pageParam + 1,
      };
    },
    {
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.nextPage <= Math.ceil(allPages[0].total / pageSize)) {
          return lastPage.nextPage;
        }
        return undefined;
      },
      onError: () =>
        notify(
          'error',
          fm({ id: 'fileUploadList.notification.title' }),
          fm({ id: 'fileUploadList.notification.unableToLoadFiles' }),
        ),
    },
  );

  const [fileToDelete, setFileToDelete] = useState<CampaignFileItem>();
  const { mutate: deleteFile, isLoading: isDeleting } = useMutation(
    async (id: string) => {
      await campaignFileService.remove(id);
    },
    {
      onSuccess: async () => {
        notifySuccess(
          fm({ id: 'fileUploadList.notification.title' }),
          fm({ id: 'fileUploadList.notification.deletedFile' }),
        );
        return queryClient.invalidateQueries('campaignFiles');
      },
      onError: () =>
        notifyError(
          fm({ id: 'fileUploadList.notification.title' }),
          fm({ id: 'fileUploadList.notification.unableToDeleteFile' }),
        ),
    },
  );
  const onConfirmDelete = () => {
    if (!fileToDelete) return;

    deleteFile(fileToDelete.id);
    setFileToDelete(undefined);
  };

  return (
    <>
      <Header title={fm({ id: 'fileUploadList.title' })}>
        <div className="col-auto">
          <Button variant="primary" onClick={() => setShowUploadModal(true)}>
            <FormattedMessage id="fileUploadList.action.upload" />
          </Button>
        </div>
      </Header>
      <div className="file-upload-list-component page-content px-5">
        {filesData?.pages?.[0]?.files?.length === 0 && (
          <div className="row p-7">
            <h3 className="col d-flex justify-content-center text-color--white">
              <FormattedMessage id="fileUploadList.noData" />
            </h3>
          </div>
        )}
        {isFilesLoading && <FileUploadListSkeleton cards={9} columns={columns} />}
        {!!filesData?.pages?.[0]?.files?.length && (
          <div className="mb-4 shadow-2">
            <div className="bg-color-gray-l3 text-uppercase p-3 rounded-top font-bold">
              <FormattedMessage id="fileUploadList.totalCount" values={{ count: filesData?.pages[0]?.total }} />
            </div>
            <InfiniteScroll
              className="rounded-bottom"
              next={fetchNextPage}
              hasMore={hasNextPage || false}
              loader={null}
              dataLength={filesData.pages.length}
              scrollableTarget="scrollContainer"
            >
              <table className="file-upload-list-table w-100">
                <TableHeader<ListFileUploadSortColumn> columns={columns} tableName="FileUpload" />
                <tbody className="animated-component-item">
                  {filesData.pages
                    .flatMap((x) => x.files)
                    .map((file) => (
                      <tr key={file.id}>
                        <td>
                          <GenericFormattedDate value={file.createdAt} withTime />
                        </td>
                        <td>
                          <TitleTooltip
                            titleTooltip={file.productName}
                            title={file.productName}
                            titleClassName="inline-crop"
                          />
                        </td>
                        <td>
                          <TitleTooltip
                            titleTooltip={file.campaignName}
                            title={file.campaignName}
                            titleClassName="inline-crop"
                          />
                        </td>
                        <td className="td-width">
                          <div className="td-width-file-name">
                            <TitleTooltip
                              titleTooltip={file.fileName}
                              title={file.fileName}
                              titleClassName="inline-crop"
                            />
                          </div>
                        </td>
                        <td>
                          <TitleTooltip titleTooltip={file.type} title={file.type} titleClassName="inline-crop" />
                        </td>
                        <td className="td-width">
                          <div className="td-width-file-comment">
                            <TitleTooltip
                              titleTooltip={file.comment}
                              title={file.comment}
                              titleClassName="inline-crop"
                            />
                          </div>
                        </td>
                        <td>
                          <TitleTooltip titleTooltip={file.status} title={file.status} titleClassName="inline-crop" />
                        </td>
                        <td align="right">
                          {file.status === CampaignFileStatus.New && (
                            <ActionsDropdown>
                              <Dropdown.Item
                                className="text-uppercase d-flex align-items-center"
                                onClick={() => setFileToDelete(file)}
                              >
                                <FontAwesomeIcon icon={faTrash} className="pe-3" />
                                <FormattedMessage id="fileUploadList.action.delete" />
                              </Dropdown.Item>
                              <DownloadFileDropdownItem
                                downloadFile={() => campaignFileService.manufacturerDownload(file.id)}
                                onLoadingChange={setFileDownloading}
                              >
                                <FontAwesomeIcon icon={faDownload} className="pe-3" />
                                <FormattedMessage id="fileUploadList.action.download" />
                              </DownloadFileDropdownItem>
                            </ActionsDropdown>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </InfiniteScroll>
          </div>
        )}
        {showUploadModal && <UploadFileModal onClose={() => setShowUploadModal(false)} />}
        {fileToDelete && (
          <Confirm
            title={fm({ id: 'fileUploadList.confirm.delete.title' })}
            targetName={fileToDelete.fileName}
            confirmTitle={fm({ id: 'fileUploadList.confirm.delete.confirmMessage' })}
            message={fm({ id: 'fileUploadList.confirm.delete.message' })}
            onClose={() => setFileToDelete(undefined)}
            onConfirm={() => onConfirmDelete()}
          />
        )}
        {(isDeleting || isFileDownloading) && <Spinner />}
      </div>
    </>
  );
};

export default FileUploadList;
